import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';

interface NotificationData {
  recipient: string;
  actor: string;
  type: 'like_post' | 'like_comment' | 'comment' | 'follow' | 'mention' | 'like' | 'bookmark';
  post?: string;
  comment?: string;
  postId?: string;
  postContent?: string;
}

export type NotificationType = 'like' | 'bookmark' | 'follow' | 'comment';

export interface Notification {
  id: string;
  type: NotificationType;
  fromUserId: string;
  fromUserName: string;
  toUserId: string;
  postId?: string;
  postContent?: string;
  createdAt: Date;
  read: boolean;
}

export const createNotification = async (
  type: 'like' | 'bookmark',
  fromUser: {
    id: string;
    displayName: string;
  },
  toUserId: string,
  post: {
    id: string;
    content: string;
  }
) => {
  try {
    const notificationData = {
      type,
      fromUser,
      toUserId,
      post,
      createdAt: serverTimestamp(),
      read: false
    };

    await addDoc(collection(db, 'notifications'), notificationData);
  } catch (error) {
    console.error('Error creating notification:', error);
  }
};

export const getUserNotifications = async (userId: string) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('toUserId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Notification[];
  } catch (error) {
    console.error('Error getting notifications:', error);
    return [];
  }
};

export const getNotifications = async (userId: string) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('recipient', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting notifications:', error);
    throw error;
  }
};

export const markNotificationsAsRead = async (userId: string, notificationIds?: string[]) => {
  try {
    if (notificationIds) {
      // Mark specific notifications as read
      await Promise.all(
        notificationIds.map(id =>
          updateDoc(doc(db, 'notifications', id), { read: true })
        )
      );
    } else {
      // Get all unread notifications
      const q = query(
        collection(db, 'notifications'),
        where('recipient', '==', userId),
        where('read', '==', false)
      );
      
      const snapshot = await getDocs(q);
      
      // Mark all as read
      await Promise.all(
        snapshot.docs.map(doc =>
          updateDoc(doc.ref, { read: true })
        )
      );
    }
  } catch (error) {
    console.error('Error marking notifications as read:', error);
    throw error;
  }
};

export const getUnreadCount = async (userId: string) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('recipient', '==', userId),
      where('read', '==', false)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('Error getting unread count:', error);
    throw error;
  }
};

export const deleteNotification = async (
  recipientId: string,
  actorId: string,
  type: NotificationData['type'],
  refId?: string
) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('recipient', '==', recipientId),
      where('actor', '==', actorId),
      where('type', '==', type)
    );
    
    const snapshot = await getDocs(q);
    
    await Promise.all(
      snapshot.docs.map(async doc => {
        const data = doc.data();
        if (
          (type === 'like_post' && data.post === refId) ||
          (type === 'like_comment' && data.comment === refId) ||
          type === 'follow'
        ) {
          await deleteDoc(doc.ref);
        }
      })
    );
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};
